import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ticketService, hotelService, teamService } from '../../services/api';
import toast from 'react-hot-toast';

const TicketCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [priorities, setPriorities] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm();

  const selectedHotelId = watch('hotelId');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [priorityRes, hotelRes] = await Promise.all([
          ticketService.getPriorities(),
          hotelService.getAll({ isActive: true })
        ]);
        setPriorities(priorityRes.data.data);
        setHotels(hotelRes.data.data);
      } catch (error) {
        console.error('Failed to fetch form data:', error);
      }
    };
    fetchData();
  }, []);

  // Fetch teams when hotel changes
  useEffect(() => {
    const fetchTeams = async () => {
      if (!selectedHotelId) {
        setTeams([]);
        return;
      }

      setTeamsLoading(true);
      try {
        const response = await teamService.getByHotel(selectedHotelId);
        setTeams(response.data.data || []);
      } catch (error) {
        console.error('Failed to fetch teams:', error);
        setTeams([]);
      } finally {
        setTeamsLoading(false);
      }
    };

    // Clear the team selection when hotel changes
    setValue('teamId', '');
    fetchTeams();
  }, [selectedHotelId, setValue]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const response = await ticketService.create(data);
      toast.success('Ticket created successfully!');
      navigate(`/tickets/${response.data.data.id}`);
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to create ticket';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Create New Ticket</h1>
        <p className="text-gray-600">Submit a support request to the appropriate team</p>
      </div>

      <div className="card">
        <form onSubmit={handleSubmit(onSubmit)} className="card-body space-y-6">
          <div>
            <label htmlFor="subject" className="label">Subject *</label>
            <input
              id="subject"
              type="text"
              className={`input ${errors.subject ? 'input-error' : ''}`}
              placeholder="Brief description of your issue"
              {...register('subject', {
                required: 'Subject is required',
                minLength: { value: 5, message: 'Subject must be at least 5 characters' },
                maxLength: { value: 500, message: 'Subject must be less than 500 characters' }
              })}
            />
            {errors.subject && (
              <p className="mt-1 text-sm text-red-600">{errors.subject.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="description" className="label">Description *</label>
            <textarea
              id="description"
              rows={6}
              className={`input ${errors.description ? 'input-error' : ''}`}
              placeholder="Please provide detailed information about your request..."
              {...register('description', {
                required: 'Description is required',
                minLength: { value: 10, message: 'Description must be at least 10 characters' }
              })}
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="hotelId" className="label">Property *</label>
              <select
                id="hotelId"
                className={`input ${errors.hotelId ? 'input-error' : ''}`}
                {...register('hotelId', { required: 'Please select a property' })}
              >
                <option value="">Select Property</option>
                {hotels.map((hotel) => (
                  <option key={hotel.id} value={hotel.id}>
                    {hotel.name} ({hotel.code})
                  </option>
                ))}
              </select>
              {errors.hotelId && (
                <p className="mt-1 text-sm text-red-600">{errors.hotelId.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="teamId" className="label">Team *</label>
              <select
                id="teamId"
                className={`input ${errors.teamId ? 'input-error' : ''}`}
                disabled={!selectedHotelId || teamsLoading}
                {...register('teamId', { required: 'Please select a team' })}
              >
                <option value="">
                  {!selectedHotelId
                    ? 'Select a property first'
                    : teamsLoading
                    ? 'Loading teams...'
                    : 'Select Team'}
                </option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
              {errors.teamId && (
                <p className="mt-1 text-sm text-red-600">{errors.teamId.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="priorityId" className="label">Priority</label>
              <select id="priorityId" className="input" {...register('priorityId')}>
                <option value="">Select Priority</option>
                {priorities.map((priority) => (
                  <option key={priority.id} value={priority.id}>
                    {priority.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="category" className="label">Category</label>
              <select id="category" className="input" {...register('category')}>
                <option value="">Select Category</option>
                <option value="Hardware">Hardware</option>
                <option value="Software">Software</option>
                <option value="Network">Network</option>
                <option value="Email">Email</option>
                <option value="Access">Access / Permissions</option>
                <option value="HR">HR Request</option>
                <option value="Facilities">Facilities</option>
                <option value="Finance">Finance</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end space-x-4 pt-4 border-t">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
            >
              {loading ? 'Creating...' : 'Create Ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TicketCreate;
